import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons"
import {
  Button,
  Container,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Icon,
  Image,
  Input,
  InputGroup,
  InputRightElement,
  Text,
  useBoolean,
} from "@chakra-ui/react"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { useForm, type SubmitHandler } from "react-hook-form"

import Logo from "/assets/images/denborgin-logo.svg"
import usePlatformAuth from "../../hooks/usePlatformAuth"
import { isPlatformLoggedIn, isTenantSubdomainHost } from "../../utils/platformAuth"
import { emailPattern } from "../../utils"

type PlatformLoginForm = {
  email: string
  password: string
}

export const Route = createFileRoute("/platform/login")({
  component: PlatformLoginPage,
  beforeLoad: async () => {
    if (isTenantSubdomainHost()) {
      throw redirect({ to: "/login" })
    }
    if (isPlatformLoggedIn()) {
      throw redirect({ to: "/platform/spaces" })
    }
  },
})

function PlatformLoginPage() {
  const [show, setShow] = useBoolean()
  const { loginMutation, error, resetError } = usePlatformAuth()
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<PlatformLoginForm>({
    defaultValues: {
      email: "",
      password: "",
    },
  })

  const onSubmit: SubmitHandler<PlatformLoginForm> = async (data) => {
    resetError()
    await loginMutation.mutateAsync(data)
  }

  return (
    <Container
      as="form"
      onSubmit={handleSubmit(onSubmit)}
      h="100vh"
      maxW="sm"
      alignItems="stretch"
      justifyContent="center"
      gap={4}
      centerContent
    >
      <Image src={Logo} alt="Denborgin logo" height="auto" maxW="2xs" alignSelf="center" mb={4} />
      <Heading size="lg" textAlign="center">Platform Admin</Heading>
      <Text color="gray.500" textAlign="center" mb={2}>
        Logowanie operatora SaaS na głównej domenie.
      </Text>

      <FormControl id="email" isInvalid={!!errors.email || !!error}>
        <FormLabel>Email</FormLabel>
        <Input
          {...register("email", {
            required: "Email is required",
            pattern: emailPattern,
          })}
          placeholder="admin@denborgin.com"
          type="email"
        />
        {errors.email ? <FormErrorMessage>{errors.email.message}</FormErrorMessage> : null}
      </FormControl>

      <FormControl id="password" isInvalid={!!errors.password || !!error}>
        <FormLabel>Password</FormLabel>
        <InputGroup>
          <Input
            {...register("password", {
              required: "Password is required",
            })}
            type={show ? "text" : "password"}
            placeholder="Password"
          />
          <InputRightElement color="ui.dim" _hover={{ cursor: "pointer" }}>
            <Icon
              as={show ? ViewOffIcon : ViewIcon}
              onClick={setShow.toggle}
              aria-label={show ? "Hide password" : "Show password"}
            />
          </InputRightElement>
        </InputGroup>
        {errors.password ? <FormErrorMessage>{errors.password.message}</FormErrorMessage> : null}
        {error ? <FormErrorMessage>{error}</FormErrorMessage> : null}
      </FormControl>

      <Button variant="primary" type="submit" isLoading={isSubmitting || loginMutation.isPending} w="full">
        Log In
      </Button>
    </Container>
  )
}
